/** @format */

import {
	AnalyticsService,
	BreakpointsService,
	CookieService,
	DeviceService,
	FileService,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import {
	Component,
	ComponentRef,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	TemplateRef,
	computed,
	effect,
	inject,
	viewChild
} from '@angular/core';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import {
	HmfAlertBannerComponent,
	HmfButtonComponent,
	HmfDrawerComponent,
	HmfModalFactoryService
} from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconBrandingDsgMono,
	homefieldIconChainLink,
	homefieldIconNikeLogo,
	homefieldIconNikeMember,
	homefieldIconNikeShoe,
	homefieldIconRewards
} from '@dsg-tech/homefield-angular-icons';

import { ActivatedRoute } from '@angular/router';
import { BreakPointNew } from '@shared/data-access/enums';
import { CommonModule } from '@angular/common';
import { BypassSanitizationPipe } from '@shared/utils/pipes';
import { StickyBannerComponent } from '../sticky-banner/sticky-banner.component';

@Component({
	selector: 'shared-component-exclusive-product-panel',
	templateUrl: './exclusive-product-panel.component.html',
	styleUrl: './exclusive-product-panel.component.scss',
	imports: [
		CommonModule,
		HmfAlertBannerComponent,
		HmfButtonComponent,
		HomefieldIconLibraryModule,
		BypassSanitizationPipe,
		StickyBannerComponent
	]
})
export class ExclusiveProductPanelComponent implements OnInit, OnChanges {
	private readonly fileService = inject(FileService);
	private readonly utilityService = inject(UtilityService);
	private readonly route = inject(ActivatedRoute);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly cookieService = inject(CookieService);
	private readonly deviceService = inject(DeviceService);
	private readonly window = inject(WINDOW) as Window;
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly breakpointsService = inject(BreakpointsService);

	@Input() brand: string;

	@Input() ctaType: string;

	@Input() buttonText: string;

	@Input() onConfirmation;

	/**
	 * @description String representing the access state that will be displayed
	 *  to the user (ex. unlocked or locked).
	 */
	@Input() accessState: string;

	@Input() infoOnly = false;

	@Input() hasStickyBanner = false;

	@Input() mobileOnlyStickyBanner = false;

	private drawerRef: ComponentRef<HmfDrawerComponent>;
	readonly drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');

	exclusiveContentFileName = 'exclusive-content.json';
	alert: any;
	content: any;
	signInParam: boolean;
	isAOS: boolean;
	readonly isMobile = computed(
		() => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM
	);
	contentBackground: string;
	contentLogo: string;
	iconFillColor = '';

	ALERT_CONTENT = {
		FAILED: {
			messageTitle: `We're sorry, something unexpected happened while connecting your accounts, please try again.`,
			messageBody: '',
			messageType: 'alert'
		},
		DUPLICATE_PARTNER_ID: {
			messageTitle: `This Nike account is already linked to a different ScoreCard account. Please try a different Nike account.`,
			messageBody: '',
			messageType: 'alert'
		}
	};

	constructor(homefieldIconsLibraryService: HomefieldIconsLibraryService) {
		homefieldIconsLibraryService.registerIcons([
			homefieldIconNikeLogo,
			homefieldIconBrandingDsgMono,
			homefieldIconChainLink,
			homefieldIconNikeShoe,
			homefieldIconNikeMember,
			homefieldIconRewards
		]);

		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.drawerRef) {
				this.drawerRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
			}
			this.setContent();
		});
	}

	ngOnInit(): void {
		if (typeof this.route?.queryParams !== 'undefined') {
			this.utilityService.isTruthy(
				this.route.queryParams.subscribe((value) => {
					if (this.ALERT_CONTENT[value.CONNECTION_STATUS]) {
						this.openDrawer();
						this.alert = this.ALERT_CONTENT[value.CONNECTION_STATUS];
					} else if (value.signIn) {
						this.signInParam = true;
					}
				})
			);
		}
		// check if user is on AOS device
		if (this.cookieService.get('DSG_VH') === '112') {
			this.isAOS = true;
		} else {
			this.isAOS = false;
		}

		const EXCLUSIVE_CONTENT = this.fileService.getDataFileByType$(
			this.exclusiveContentFileName,
			'json'
		);

		EXCLUSIVE_CONTENT.subscribe((values) => {
			this.content = values[this.brand];

			this.setContent();

			if (this.content) {
				this.iconFillColor = this.getIconFillColor();
			}
		});
	}

	private setContent() {
		if (this.isMobile()) {
			this.contentBackground = this.content?.mobile_background;
			this.contentLogo = this.content?.mobile_logo;
		} else {
			this.contentBackground = this.content?.desktop_background;
			this.contentLogo = this.content?.desktop_logo;
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			changes.ctaType &&
			changes.ctaType.currentValue &&
			this.signInParam &&
			this.ctaType === 'connectAccounts'
		) {
			this.openDrawer();
		}
	}

	doAction(action: string) {
		let path = this.window.location.href.split('?')[0];
		if (action === 'scorecardLogin') {
			path += '?signIn=true';
			window.authFunctions.login(path);
		} else if (action === 'nikeLogin') {
			window.connectionFunctions.connectAthleteToNike(path);
		} else if (action === 'connectedUser') {
			window.location.href = '/f/all-nike-connected-membership';
		}
	}

	triggerAnalytics() {
		/*
		 * Analytics Event
		 */

		// Set the modal name
		this.analyticsService.setModalName('ConnectAccounts');

		// Set the Analytics event type
		this.analyticsService.setType('event', 'ModalOpen');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('ModalOpen');
	}

	/**
	 * @description Retrieves header text based on the access state of
	 * the user and if use is on mobile or desktop
	 */
	getHeaderText() {
		if (this.deviceService.isMobile()) {
			return this.content[this.accessState]?.mobileHeaderText;
		} else {
			return this.content[this.accessState]?.headerText;
		}
	}
	getAOSHeaderText() {
		if (this.deviceService.isMobile()) {
			return this.content[this.accessState]?.aosMobileHeaderText;
		} else {
			return this.content[this.accessState]?.aosHeaderText;
		}
	}

	getIconFillColor(): string {
		const className = this.content[this.accessState]?.iconClassName;
		const iconClassMap = {
			'unlock-icon-color': 'var(--hmf-scgreen)',
			'lock-icon-color': 'var(--hmf-gray500)',
			'gold-unlock-icon-color': '#C4AE71'
		};

		return iconClassMap[className] || '';
	}

	trackByFn(index: number, item: any): string {
		return index.toString() + item.dataEventName;
	}

	openDrawer() {
		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: this.content?.panelContent?.headerText,
					closable: true,
					mobilePeek: true,
					direction: 'right',
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.drawerContent()
			);
		}

		this.drawerRef.instance.openDrawer();
	}
}
